import { ImageDetailsPageTabs } from 'app/plugins/openstack/components/images/details/model'
import {
  TableHeaderTooltip,
  TPMModelText,
} from 'app/plugins/openstack/components/images/ImagesListPage'
import ImageStatusInfo from 'app/plugins/openstack/components/images/ImageStatusInfo'
import { IVolumeSnapshotDetailsPageTabs } from 'app/plugins/openstack/components/storage/snapshots/details/model'
import { IVmDetailsPageTabs } from 'app/plugins/openstack/components/vms/vm-details/model'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { humanReadableSize } from 'openstack/helpers'
import React from 'react'
import { durationBetweenDates } from 'utils/misc'

export const usefulImageProperties = [
  'architecture',
  'hw_boot_menu',
  'hw_cdrom_bus',
  'hw_cpu_cores',
  'hw_cpu_policy',
  'hw_cpu_sockets',
  'hw_cpu_thread_policy',
  'hw_cpu_threads',
  'hw_disk_bus',
  'hw_firmware_type',
  'hw_machine_type',
  'hw_mem_encryption',
  'hw_pmu',
  'hw_pointer_model',
  'hw_rng_model',
  'hw_scsi_model',
  'hw_serial_port_count',
  'hw_time_hpet',
  'hw_video_model',
  'hw_video_ram',
  'hw_vif_model',
  'hw_vif_multiqueue_enabled',
  'hw_watchdog_action',
  'hypervisor_type',
  'img_config_drive',
  'img_hide_hypervisor_id',
  'img_type',
  'instance_uuid',
  'kernel_id',
  'os_admin_user',
  'os_command_line',
  'os_distro',
  'os_secure_boot',
  'os_shutdown_timeout',
  'os_type',
  'os_version',
  'ramdisk_id',
  'rootfs_uuid',
  'trait:',
  'vm_mode',
  'vmware_adaptertype',
  'vmware_ostype',
]

export const gridSearchFn = (value, searchString) => {
  if (!searchString) return false
  if (!value || typeof value !== 'object') return false

  const lowerSearch = searchString?.toLowerCase()?.trim()

  // Check for key=value format
  if (lowerSearch?.includes('=')) {
    const [searchKey, searchValueRaw] = lowerSearch?.split('=')?.map((s) => s?.trim())
    const v = value[searchKey]
    if (v === undefined) return false
    const vLower = String(v)?.toLowerCase()
    // If value part is empty, match any value for that key
    if (!searchValueRaw) return true
    return vLower?.startsWith(searchValueRaw) // Use startsWith for partial value matching
  }

  // fallback: search key or value anywhere
  return Object.keys(value)?.some((k) => {
    const kStr = String(k)?.toLowerCase()
    const vStr = String(value[k])?.toLowerCase()
    return kStr?.includes(lowerSearch) || vStr?.includes(lowerSearch)
  })
}

export const rescueImageTooltip =
  'Enable this option to use the image for VM rescue operations with hw_rescue_device=disk and hw_rescue_bus=scsi.'

export const getVmSnapshotColumns = ({ vmsById, volumeSnapshotsById, hideKeys = ['id'] }) => [
  {
    key: 'name',
    label: 'Name',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id, name }) =>
        !!name
          ? routeManager.routes.openstack.imageDetails.path({
              id,
              detailsTab: ImageDetailsPageTabs.Overview,
            })
          : '',
    }),
    display: hideKeys.includes('name') ? false : true,
  },
  {
    key: 'id',
    label: 'UUID',
    display: hideKeys.includes('id') ? false : true,
  },
  {
    key: 'status',
    label: (
      <TitleWithInfoIcon
        titleText="Status"
        tooltipMsg={<ImageStatusInfo entityType={'VM snapshot'} />}
      />
    ),
    display: hideKeys.includes('status') ? false : true,
  },
  {
    key: 'imageTypeDisplay',
    label: 'Type',
    display: hideKeys.includes('imageTypeDisplay') ? false : true,
  },
  {
    key: 'instance_uuid',
    label: 'Source VM',
    render: (val) =>
      val ? (
        <SimpleLink
          src={routeManager.routes.openstack.vmDetails.path({
            id: val,
            tab: IVmDetailsPageTabs.Overview,
          })}
        >
          {vmsById?.(val)?.name || val}
        </SimpleLink>
      ) : null,
    display: hideKeys.includes('instance_uuid') ? false : true,
  },
  {
    key: 'snapshotIds',
    label: 'Volume Snapshots',
    render: (val) =>
      val?.length ? (
        <div style={{ display: 'grid', gap: '8px' }}>
          {val?.map((snapshotId) => (
            <div key={snapshotId}>
              <SimpleLink
                src={routeManager.routes.openstack.volumeSnapshotDetails.path({
                  id: snapshotId,
                  tab: IVolumeSnapshotDetailsPageTabs.Overview,
                })}
              >
                {volumeSnapshotsById?.[snapshotId]?.name || snapshotId}
              </SimpleLink>
            </div>
          ))}
        </div>
      ) : null,
    display: hideKeys.includes('snapshotIds') ? false : true,
  },
  {
    key: 'visibility',
    label: 'Visibility',
    display: hideKeys.includes('visibility') ? false : true,
  },
  {
    key: 'protected',
    label: 'Protected',
    render: (val) => (val ? 'True' : 'False'),
    display: hideKeys.includes('protected') ? false : true,
  },
  {
    key: 'disk_format',
    label: 'Disk Format',
    display: hideKeys.includes('disk_format') ? false : true,
  },
  {
    key: 'virtual_size',
    label: (
      <TitleWithInfoIcon
        titleText="Virtual Disk Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the VM disk that will get created from this image" />
        }
      />
    ),
    render: humanReadableSize,
    display: hideKeys.includes('virtual_size') ? false : true,
  },
  {
    key: 'size',
    label: (
      <TitleWithInfoIcon
        titleText="File Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the snapshot file that is stored in the Image Library" />
        }
      />
    ),
    render: humanReadableSize,
    display: hideKeys.includes('size') ? false : true,
  },
  {
    key: 'owner_user_name',
    label: 'Snapshot Owner',
    display: hideKeys.includes('owner_user_name') ? false : true,
  },
  {
    key: 'hw_tpm_model',
    label: 'Virtual TPM 2.0',
    render: (value, item) =>
      item?.hw_tpm_version === '2.0' ? `Enabled (${TPMModelText[value]})` : '',
    display: hideKeys.includes('hw_tpm_model') ? false : true,
  },
  {
    key: 'tags',
    label: 'Tags',
    render: (tags) => tags?.join(', '),
    display: hideKeys.includes('tags') ? false : true,
  },
  {
    key: 'properties',
    label: 'Properties',
    disableSorting: true,
    CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    display: hideKeys.includes('properties') ? false : true,
  },
  {
    key: 'created_at',
    label: 'Age',
    formatFn: (value) => durationBetweenDates({ labels: ['d', 'h'] })(value),
    display: hideKeys.includes('created_at') ? false : true,
  },
]

export const initializeResults = (items, actionName, status = 'In Progress') =>
  items?.map((item) => ({
    id: item?.id,
    name: item?.name || item?.id,
    status,
    actionName,
  })) || []

export const processResults = (key, results, items) =>
  results?.map((result, index) => ({
    id: items[index]?.id,
    name: items[index]?.name || items[index]?.id,
    status: result?.success ? 'Success' : 'Failed',
    actionName: result?.success ? `${key} Deleted` : `${key} Delete Failed`,
    error: result?.success ? null : result?.error?.message,
  })) || []
